import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MessageCircle, Clock, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useLang } from '@/context/LanguageContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ScrollReveal } from '@/components/common/Primitives';

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error(t('offer.error')); return; }
    setSubmitting(true);
    try {
      await api.post('/leads', { ...form, language: lang, type: 'contact', source_page: '/kontakt' });
      setDone(true); toast.success(t('offer.success'));
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch { toast.error(t('offer.error')); } finally { setSubmitting(false); }
  };

  const trust = [
    { icon: Clock, label: t('contact.responseTime') },
    { icon: Phone, label: '+49 (0) 800 360 360', sub: t('contact.hotline') },
    { icon: Mail, label: 'info@it-welt360.de' },
  ];

  return (
    <div className="itw-noise min-h-screen gradient-hero pt-32 lg:pt-40 pb-20">
      <div className="absolute inset-0 itw-grid opacity-40" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('contact.title')}</h1>
            <p className="mt-4 text-[color:var(--itw-text-2)]">{t('contact.subtitle')}</p>
          </div>
        </ScrollReveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <ScrollReveal className="lg:col-span-3">
            <div className="glass rounded-2xl p-6 sm:p-8">
              {done ? (
                <div className="flex flex-col items-center text-center py-12" data-testid="contact-success">
                  <CheckCircle2 className="h-14 w-14 text-[color:var(--itw-cyan)]" />
                  <p className="mt-4 text-[color:var(--itw-text)]">{t('offer.success')}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5"><Label>{t('offer.name')} *</Label><Input value={form.name} onChange={update('name')} required data-testid="contact-name-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
                    <div className="space-y-1.5"><Label>{t('offer.phone')}</Label><Input value={form.phone} onChange={update('phone')} data-testid="contact-phone-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
                  </div>
                  <div className="space-y-1.5"><Label>{t('offer.email')} *</Label><Input type="email" value={form.email} onChange={update('email')} required data-testid="contact-email-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
                  <div className="space-y-1.5"><Label>{t('offer.message')}</Label><Textarea rows={5} value={form.message} onChange={update('message')} data-testid="contact-message-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
                  <Button type="submit" disabled={submitting} data-testid="contact-submit-button"
                    className="w-full h-11 bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium neon-edge hover:opacity-90 transition-opacity">
                    {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('offer.sending')}</> : t('contact.sendMessage')}
                  </Button>
                </form>
              )}
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.1} className="lg:col-span-2">
            <div className="space-y-4">
              {trust.map((it, i) => (
                <div key={i} className="glass rounded-2xl p-5 flex items-center gap-4">
                  <div className="h-11 w-11 rounded-xl bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] grid place-items-center"><it.icon className="h-5 w-5 text-[color:var(--itw-cyan)]" /></div>
                  <div><div className="text-[color:var(--itw-text)] font-medium">{it.label}</div>{it.sub && <div className="text-xs text-[color:var(--itw-text-3)]">{it.sub}</div>}</div>
                </div>
              ))}
              <a href="https://wa.me/498003603600" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-button"
                className="glass rounded-2xl p-5 flex items-center gap-4 transition-[border-color] duration-200 hover:border-[color:var(--itw-border-strong)]">
                <div className="h-11 w-11 rounded-xl bg-[#25D366]/15 border border-[#25D366]/30 grid place-items-center"><MessageCircle className="h-5 w-5 text-[#25D366]" /></div>
                <div className="text-[color:var(--itw-text)] font-medium">{t('contact.whatsapp')}</div>
              </a>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </div>
  );
}
